// Finalise le flux OIDC au retour du provider : valide le state (anti-CSRF),
// puis délègue l'échange du code et la validation du token à l'Edge Function.

import type { NavigateFunction } from 'react-router-dom';
import toast from 'react-hot-toast';
import { clearOidcSession, loadOidcSession } from './session';
import { invokeOidcFunction } from './invoke';
import { supabase } from '../../lib/supabase';
import { useLeagueStore } from '../../lib/leagueStore';
import { hasAcceptedLeagueRules } from '../../lib/leagueRulesAcceptance';

export interface OidcUser {
  user_id: number;
  username: string;
  email: string | null;
  email_verified: boolean;
  name: string | null;
  picture: string | null;
  tier: string | null;
  founder: boolean;
  tenant_id: string | null;
}

/**
 * Échange le code d'autorisation contre les infos utilisateur.
 * @throws si le state ne correspond pas, si la session PKCE est absente,
 *         ou si l'Edge Function rejette le code/token
 */
export async function exchangeCode(code: string, state: string): Promise<OidcUser> {
  const session = loadOidcSession();
  if (!session) {
    throw new Error('Session OIDC introuvable — veuillez recommencer la connexion');
  }

  // Anti-CSRF : le state retourné par le provider doit correspondre à celui généré au départ
  if (state !== session.state) {
    clearOidcSession();
    throw new Error('Paramètre state invalide — connexion refusée');
  }

  try {
    return await invokeOidcFunction<OidcUser>('oidc-callback', {
      code,
      code_verifier: session.codeVerifier,
      nonce: session.nonce,
    });
  } finally {
    // La session PKCE est à usage unique, succès ou échec
    clearOidcSession();
  }
}

// ID de la ligue POOL BPM — rejointe automatiquement à chaque login OIDC
const BPM_LEAGUE_ID = 138;

// Après login BPM : auto-join ligue 138, sélection ligue, redirection selon état équipe.
export async function joinBpmLeagueAndRedirect(
  userId: number,
  navigate: NavigateFunction,
): Promise<void> {
  try {
    const { data: membership, error: memberError } = await supabase
      .from('leagues_users')
      .select('initial_build_complete')
      .eq('league_id', BPM_LEAGUE_ID)
      .eq('user_id', userId)
      .single();

    let buildComplete = membership?.initial_build_complete ?? false;

    if (memberError) {
      if (memberError.code !== 'PGRST116') throw memberError;
      // Pas encore membre → rejoindre automatiquement
      const { error: joinError } = await supabase.rpc('add_user_to_league', {
        p_user_id: userId,
        p_league_id: BPM_LEAGUE_ID,
      });
      if (joinError) throw joinError;
      buildComplete = false;
    }

    const { data: leagueData } = await supabase
      .from('leagues')
      .select('league_name')
      .eq('league_id', BPM_LEAGUE_ID)
      .single();

    useLeagueStore.getState().setSelectedLeague({
      id: BPM_LEAGUE_ID,
      name: leagueData?.league_name ?? 'POOL BPM',
    });

    if (!buildComplete) {
      // Première fois ou règles jamais acceptées → page d'acceptation des règles
      const rulesAccepted = hasAcceptedLeagueRules(BPM_LEAGUE_ID);
      navigate(rulesAccepted ? '/build-my-team' : '/league-rules-onboarding', { replace: true });
    } else {
      navigate('/my-office', { replace: true });
    }
  } catch (err) {
    console.error('[BPM redirect] error:', err);
    toast.error('Erreur lors de la mise en place de ta ligue. Réessaie.');
    navigate('/dashboard', { replace: true });
  }
}
